import { useEffect, useState } from "react";
import { ArrowRight, Loader2, Lock, CheckCircle2, Check, Play, X } from "lucide-react";
import { assetUrl } from "@/lib/api";
import { Speakers, Footer } from "@/components/LandingExtras";
import { HeroBanner, Takeaways, Gallery, Partners } from "@/components/LandingSections";
import { ChatWidget } from "@/components/ChatWidget";
import { FieldControl, LegalAgree, PartyLinks } from "@/components/LeadFields";
import { AdjustableImage } from "@/components/ImageAdjust";

export const HEADING_FONTS = ["Outfit", "Playfair Display", "Space Grotesk", "Bebas Neue", "Montserrat", "Lora", "Poppins", "DM Serif Display"];
export const BODY_FONTS = ["DM Sans", "Inter", "Lato", "Source Sans 3", "Merriweather", "Nunito"];

const fontHref = (f) => `https://fonts.googleapis.com/css2?family=${f.replace(/ /g, "+")}:wght@400;500;600;700;800&display=swap`;

export function useFonts(heading, body) {
  useEffect(() => {
    [heading, body].filter(Boolean).forEach((f) => {
      const id = `sv-font-${f.replace(/\s/g, "")}`;
      if (!document.getElementById(id)) {
        const l = document.createElement("link");
        l.id = id;
        l.rel = "stylesheet";
        l.href = fontHref(f);
        document.head.appendChild(l);
      }
    });
  }, [heading, body]);
}

const POSTER = "https://images.unsplash.com/photo-1612548403247-aa2873e9422d?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";
const LOGO_H = { sm: "h-7", md: "h-10", lg: "h-14" };
const TOP_SIZE = { sm: "text-xl", md: "text-2xl sm:text-3xl", lg: "text-3xl sm:text-4xl", xl: "text-4xl sm:text-5xl" };

const px = (v, vw) => (v ? { fontSize: vw ? `min(${v}px, ${vw}vw)` : `${v}px` } : {});

const TopHeader = ({ data, font, fallback, size }) => data?.text?.trim() ? (
  <h2 data-testid="landing-top-header" className={`${TOP_SIZE[data.size] || TOP_SIZE.md} font-bold tracking-tight text-center mb-8 whitespace-pre-line`}
    style={{ fontFamily: `"${font}", sans-serif`, color: data.color || fallback, ...px(size, 9) }}>{data.text}</h2>
) : null;

const SPONSOR_H = { sm: "h-6", md: "h-9", lg: "h-12" };
const SponsorLogos = ({ data, logos, dim }) => (
  <div data-testid="landing-sponsors" className="flex flex-col items-end gap-1.5 justify-self-end">
    {data.label?.trim() && <span data-testid="landing-sponsors-label" className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${dim}`}>{data.label}</span>}
    <div className="flex items-center gap-4 flex-wrap justify-end">
      {logos.map((l, i) => {
        const img = <img src={assetUrl(l.path)} alt="Sponsor" style={l.height ? { height: `${l.height}px` } : undefined}
          className={`${l.height ? "" : SPONSOR_H[data.size] || SPONSOR_H.md} w-auto max-w-[240px] object-contain`} />;
        return l.url
          ? <a key={i} data-testid={`landing-sponsor-${i}`} href={l.url} target="_blank" rel="noreferrer" className="hover:opacity-80 transition-opacity">{img}</a>
          : <span key={i} data-testid={`landing-sponsor-${i}`}>{img}</span>;
      })}
    </div>
  </div>
);

const FormPopup = ({ onClose, children }) => (
  <div data-testid="landing-form-popup" className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" onClick={onClose}>
    <div className="relative w-full max-w-lg my-8 sv-rise" onClick={(e) => e.stopPropagation()}>
      <button type="button" data-testid="landing-form-popup-close" onClick={onClose} aria-label="Close form"
        className="absolute -top-3 -right-3 z-10 w-9 h-9 rounded-full bg-white text-slate-800 grid place-items-center shadow-lg hover:scale-105 transition-transform"><X className="w-4 h-4" /></button>
      {children}
    </div>
  </div>
);

export const LandingPage = ({ campaign: c, onSubmit, preview = false }) => {
  const b = c.branding;
  const formDef = c.form || { fields: [], optins: [] };
  const light = b.theme === "light";
  const accent = b.accent;
  useFonts(b.font_heading, b.font_body);
  const [email, setEmail] = useState("");
  const [answers, setAnswers] = useState({});
  const [optins, setOptins] = useState({});
  const [agreed, setAgreed] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const fs = b.font_sizes || {};
  const popup = b.form_mode === "popup";

  const submit = async (e) => {
    e.preventDefault();
    for (const f of formDef.fields) {
      const v = answers[f.id];
      if (f.required && (v == null || v === "" || (Array.isArray(v) && !v.length))) return setErr(`"${f.label}" is required`);
    }
    for (const o of formDef.optins) if (o.required && !optins[o.id]) return setErr(`"${o.label}" must be accepted`);
    if (formDef.legal?.require_agree && formDef.legal.parties?.length && !agreed) return setErr("Please agree to the Privacy Policy and Terms & Conditions");
    setErr(""); setBusy(true);
    try { await onSubmit({ email, answers, optins, legal_agreed: agreed }); }
    catch (ex) { setErr(ex.message || "Something went wrong"); setBusy(false); }
  };

  const dim = light ? "text-slate-500" : "text-slate-400";
  const headColor = light ? "text-slate-900" : "text-white";
  const titleStyle = { fontFamily: `"${b.font_heading}", sans-serif`, ...(b.heading_color ? { color: b.heading_color } : {}), ...px(fs.title, 11) };
  const bodyFont = { fontFamily: `"${b.font_body}", sans-serif` };
  const descStyle = { ...bodyFont, ...px(fs.description) };
  const labelStyle = px(fs.form_label);
  const bg = b.bg_type === "image" && b.bg_image_path
    ? { backgroundImage: `url(${assetUrl(b.bg_image_path)})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { backgroundColor: b.bg_color };

  const lines = (c.description || "").split("\n").map((l) => l.trim()).filter(Boolean);
  const bullets = lines.filter((l) => /^[-•*]\s+/.test(l)).map((l) => l.replace(/^[-•*]\s+/, ""));
  const paras = lines.filter((l) => !/^[-•*]\s+/.test(l)).join("\n");

  const sponsors = (c.sponsors?.logos || []).filter((l) => l.path);
  const logoPos = sponsors.length && b.logo_position === "right" ? "left" : b.logo_position;
  const mainLogo = b.logo_path && <img data-testid="landing-logo" src={assetUrl(b.logo_path)} alt="Logo" className={`${LOGO_H[b.logo_size]} w-auto object-contain`} />;
  const logo = (mainLogo || sponsors.length > 0) && (
    <div data-testid="landing-logo-bar" className={`mb-8 ${sponsors.length ? "grid grid-cols-[1fr_auto_1fr] items-center gap-4" : `flex ${logoPos === "center" ? "justify-center" : logoPos === "right" ? "justify-end" : "justify-start"}`}`}>
      {sponsors.length ? <>
        <div className="flex justify-start">{logoPos === "left" && mainLogo}</div>
        <div className="flex justify-center">{logoPos === "center" && mainLogo}</div>
        <SponsorLogos data={c.sponsors} logos={sponsors} dim={dim} />
      </> : mainLogo}
    </div>
  );

  const poster = (
    <div className="relative">
      <div className="absolute -inset-px rounded-2xl opacity-40" style={{ background: `linear-gradient(135deg, ${accent}, transparent)` }} />
      <div className="relative rounded-2xl overflow-hidden bg-black aspect-video">
        {c.thumbnail_path
          ? <AdjustableImage src={assetUrl(c.thumbnail_path)} st={{ ...(c.image_styles?.thumbnail || {}), height: 0 }} alt="Video preview" className="w-full h-full" imgClass="opacity-80" />
          : <img src={POSTER} alt="Video preview" className="w-full h-full object-cover opacity-50 blur-[2px] scale-105" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        <div className="absolute inset-0 grid place-items-center">
          <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 grid place-items-center"><Lock className="w-6 h-6 text-white" /></div>
        </div>
        <div className="absolute bottom-4 left-4 right-4 text-xs text-white">
          <span className="flex w-fit items-center gap-1.5 bg-black/50 backdrop-blur px-3 py-1.5 rounded-full"><Play className="w-3 h-3" /> Complete the form to unlock</span>
        </div>
      </div>
    </div>
  );

  const speakersEl = (
    <Speakers heading={c.speakers_heading} speakers={c.speakers} light={light} accent={accent} headingFont={b.font_heading} bodyFont={b.font_body} />
  );

  const content = (
    <div>
      <h1 data-testid="landing-title" className={`font-extrabold tracking-tight leading-[1.06] text-4xl sm:text-5xl lg:text-6xl ${headColor}`} style={titleStyle}>{c.title}</h1>
      {c.headline && <p data-testid="landing-headline" className={`text-lg mt-6 ${light ? "text-slate-600" : "text-slate-300"}`} style={bodyFont}>{c.headline}</p>}
      {paras && <p data-testid="landing-description" className={`text-sm sm:text-base mt-4 leading-relaxed whitespace-pre-line ${dim}`} style={descStyle}>{paras}</p>}
      {bullets.length > 0 && (
        <ul data-testid="landing-bullets" className="mt-6 space-y-3 max-w-xl">
          {bullets.map((x, i) => (
            <li key={i} className={`flex items-start gap-3 text-sm sm:text-base ${light ? "text-slate-700" : "text-slate-300"}`} style={descStyle}>
              <span className="mt-0.5 w-5 h-5 rounded-full grid place-items-center shrink-0" style={{ background: `${accent}26`, color: accent }}><Check className="w-3 h-3" /></span>
              {x}
            </li>
          ))}
        </ul>
      )}
      {speakersEl}
      {!c.hide_video_thumbnail && <div className="mt-10 max-w-md">{poster}</div>}
    </div>
  );

  const formCard = (
    <form onSubmit={submit} data-testid="public-lead-form" className={`rounded-2xl border backdrop-blur-xl overflow-hidden ${light ? "bg-white/90 border-slate-200 text-slate-900 shadow-2xl shadow-slate-900/5" : "bg-white/[0.04] border-white/10 text-white"}`}>
      <div className="h-1.5 w-full" style={{ background: accent }} />
      <div className="p-6 sm:p-8 space-y-4">
        <h3 data-testid="landing-form-heading" className={`font-bold text-xl tracking-tight ${headColor}`} style={{ fontFamily: `"${b.font_heading}", sans-serif`, ...px(fs.form_heading) }}>
          {c.form_heading || "Complete the form below"}
        </h3>
        <div className="grid grid-cols-2 gap-x-3 gap-y-4" data-testid="landing-form-fields">
          <div className="col-span-2">
            <label className={`block font-mono text-[11px] uppercase tracking-widest mb-2 ${dim}`} style={labelStyle}>Business Email *</label>
            <input data-testid="public-lead-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
              className={`w-full h-11 px-4 rounded-md text-sm focus:outline-none border transition-colors ${light ? "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400" : "bg-white/5 border-white/10 text-white placeholder:text-slate-500"}`} />
          </div>
          {formDef.fields.map((f) => (
            <div key={f.id} data-testid={`lead-field-wrap-${f.id}`} className={f.width === "half" ? "col-span-2 sm:col-span-1 min-w-0" : "col-span-2"}>
              <label className={`block font-mono text-[11px] uppercase tracking-widest mb-2 ${dim}`} style={labelStyle}>{f.label}{f.required ? " *" : ""}</label>
              <FieldControl field={f} value={answers[f.id]} onChange={(v) => setAnswers((a) => ({ ...a, [f.id]: v }))} light={light} accent={accent} />
            </div>
          ))}
        </div>
        {formDef.optins.length > 0 && (
          <div className={`pt-3 space-y-2.5 border-t ${light ? "border-slate-200" : "border-white/10"}`}>
            {formDef.optins.map((o) => (
              <label key={o.id} data-testid={`lead-optin-${o.id}`} className={`flex items-start gap-2.5 text-xs leading-relaxed cursor-pointer ${dim}`}>
                <input type="checkbox" className="mt-0.5" checked={!!optins[o.id]} style={{ accentColor: accent }} onChange={(e) => setOptins((s) => ({ ...s, [o.id]: e.target.checked }))} />
                <span>{o.label}<PartyLinks parties={formDef.legal?.parties || []} idx={o.parties || []} prefix={`optin-${o.id}`} />{o.required ? " *" : ""}</span>
              </label>
            ))}
          </div>
        )}
        <LegalAgree legal={formDef.legal} checked={agreed} onChange={setAgreed} dim={dim} accent={accent} />
        {err && <p data-testid="public-lead-error" className="text-sm text-rose-400">{err}</p>}
        <button data-testid="public-lead-submit-button" disabled={busy}
          className={`w-full h-12 ${b.button_style === "pill" ? "rounded-full" : "rounded-none"} disabled:opacity-60 font-semibold text-white flex items-center justify-center gap-2 transition-opacity hover:opacity-90`}
          style={{ background: accent, boxShadow: `0 0 40px -8px ${accent}B3` }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{c.cta_text || "Watch now"} <ArrowRight className="w-4 h-4" /></>}
        </button>
        <div className={`flex flex-wrap gap-x-5 gap-y-2 text-xs ${dim}`}>
          <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" style={{ color: accent }} /> Instant access</span>
          <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" style={{ color: accent }} /> No spam, ever</span>
        </div>
      </div>
    </form>
  );

  const ctaCard = (
    <div data-testid="landing-cta-card" className={`rounded-2xl border backdrop-blur-xl overflow-hidden ${light ? "bg-white/90 border-slate-200 text-slate-900 shadow-2xl shadow-slate-900/5" : "bg-white/[0.04] border-white/10 text-white"}`}>
      <div className="h-1.5 w-full" style={{ background: accent }} />
      <div className="p-6 sm:p-8 space-y-5">
        <h3 className={`font-bold text-xl tracking-tight ${headColor}`} style={{ fontFamily: `"${b.font_heading}", sans-serif`, ...px(fs.form_heading) }}>{c.form_heading || "Complete the form below"}</h3>
        <button type="button" data-testid="landing-cta-open-form" onClick={() => setPopupOpen(true)}
          className={`w-full h-12 ${b.button_style === "pill" ? "rounded-full" : "rounded-none"} font-semibold text-white flex items-center justify-center gap-2 transition-opacity hover:opacity-90`}
          style={{ background: accent, boxShadow: `0 0 40px -8px ${accent}B3` }}>
          {c.cta_text || "Watch now"} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
  const formSlot = popup ? ctaCard : formCard;

  const centered = b.layout === "center";
  return (
    <div className={`relative min-h-screen overflow-clip ${headColor}`} style={{ ...bg, ...bodyFont }}>
      {b.bg_type === "image" && <div className="absolute inset-0" style={{ background: light ? "rgba(255,255,255,0.82)" : "rgba(5,5,7,0.82)" }} />}
      {!light && <div className="sv-glow-orb w-[520px] h-[520px] -top-40 -left-40" style={{ background: `${accent}33` }} />}
      {!light && <div className="sv-glow-orb w-[600px] h-[600px] bg-violet-600/20 -bottom-60 -right-40" />}
      <div className={`relative z-10 max-w-6xl mx-auto px-6 ${preview ? "py-10" : "pt-10 pb-12 lg:pt-14 lg:pb-14"}`}>
        {logo}
        <TopHeader data={c.top_header} font={b.font_heading} fallback={b.heading_color || undefined} size={fs.header} />
        <HeroBanner path={c.hero_image_path} st={c.image_styles?.hero} />
        {centered ? (
          <div className="max-w-2xl mx-auto space-y-10">
            <div className="text-center">
              <h1 data-testid="landing-title" className={`font-extrabold tracking-tight leading-[1.06] text-4xl sm:text-5xl lg:text-6xl ${headColor}`} style={titleStyle}>{c.title}</h1>
              {c.headline && <p data-testid="landing-headline" className={`text-lg mt-6 ${light ? "text-slate-600" : "text-slate-300"}`} style={bodyFont}>{c.headline}</p>}
              {paras && <p data-testid="landing-description" className={`text-sm sm:text-base mt-4 leading-relaxed whitespace-pre-line ${dim}`} style={descStyle}>{paras}</p>}
            </div>
            {bullets.length > 0 && (
              <ul data-testid="landing-bullets" className="space-y-3 max-w-xl mx-auto text-left">
                {bullets.map((x, i) => (
                  <li key={i} className={`flex items-start gap-3 text-sm sm:text-base ${light ? "text-slate-700" : "text-slate-300"}`} style={descStyle}>
                    <span className="mt-0.5 w-5 h-5 rounded-full grid place-items-center shrink-0" style={{ background: `${accent}26`, color: accent }}><Check className="w-3 h-3" /></span>
                    {x}
                  </li>
                ))}
              </ul>
            )}
            <div className="text-left">{formSlot}</div>
            <div className="text-left">{speakersEl}</div>
            {!c.hide_video_thumbnail && poster}
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-14 items-start">
            <div data-testid="landing-content-panel" className={b.layout === "form-left" ? "lg:order-2" : ""}>{content}</div>
            <div data-testid="landing-form-panel" className={`${b.layout === "form-left" ? "lg:order-1" : ""} lg:sticky lg:top-10`}>{formSlot}</div>
          </div>
        )}
        <Takeaways data={c.takeaways} light={light} accent={accent} headingFont={b.font_heading} st={c.image_styles?.takeaways} />
        <Gallery data={c.gallery} light={light} headingFont={b.font_heading} />
        <Partners data={c.partners} light={light} />
      </div>
      <Footer text={c.footer_text} light={light} />
      {popup && popupOpen && <FormPopup onClose={() => setPopupOpen(false)}>{formCard}</FormPopup>}
      <ChatWidget slug={c.slug} chat={c.chat} preview={preview} />
    </div>
  );
};
